import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getPalletsWithDefects, getPalletsWithDefectsByPalletIds } from "../../aplApi/palletsHelper";
import { PalletDetail } from "../../types/palletDetail";
import { ConsumptionDetail } from "../../types/consumptionDetail";
import _ from "lodash";
import { Guid } from "guid-typescript";
import { ModalType } from "../../components/common/Modals/types/modalType";
import { ReduxStateModalPallets2 } from "../../types/redux/reduxStateModalPallets2";
import { initialStateModalPallets2 } from "../defaultState";
import { getConsumptionState } from "../../aplApi/consumptionsHelper";
import { getAplError } from "../../aplApi/exceptionHelper";
import { onSetConsumptionOperationError } from "./consumptionDetails.feature";

export const showModalWithPalletsByObject = createAsyncThunk(
    "modalPallets2/showModalWithPalletsByObject",
    async (props: { pallets: PalletDetail[] | ConsumptionDetail[]; modalType: ModalType }, thunkApi) => {
        try {
            return (await getPalletsWithDefects(props.pallets)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const showModalWithPalletsByGuid = createAsyncThunk(
    "modalPallets2/showModalWithPalletsByGuid",
    async (props: { pallets: Guid[]; modalType: ModalType; callerModalType: ModalType }, thunkApi) => {
        try {
            return (await getPalletsWithDefectsByPalletIds(props.pallets)).data;
            
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const showModalWithConsumptionState = createAsyncThunk(
    "modalPallets2/showModalWithConsumptionState",
    async (props: { operationId: string; barcode: string }, thunkApi) => {
        try {
            thunkApi.dispatch(onSetConsumptionOperationError(null));
            return await getConsumptionState(props.operationId, props.barcode);
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

const modalPallets2 = createSlice({
    name: "modalPallets2",
    initialState: initialStateModalPallets2,
    reducers: {
        showModalWithoutPallets: (
            state,
            action: {
                payload: {
                    modal: ModalType;
                    caller: ModalType;
                };
                type: string;
            }
        ): ReduxStateModalPallets2 => ({ ...state, modalShown: action.payload.modal, caller: action.payload.caller }),
        closeModalRedux: (state): ReduxStateModalPallets2 => ({
            ...initialStateModalPallets2,
            pallets: [],
            modalShown: state.caller,
            caller: ModalType.None,
            consumptionState: null
        }),
        setModified: (state, action): ReduxStateModalPallets2 => ({ ...state, modified: action.payload }),
        setModalPallets: (state, action): ReduxStateModalPallets2 => ({ ...state, pallets: action.payload }),
        setModalMessage: (state, action): ReduxStateModalPallets2 => ({ ...state, message: action.payload }),
        setModalError: (state, action): ReduxStateModalPallets2 => ({ ...state, error: action.payload })
    },
    extraReducers: builder => {
        builder
            .addCase(showModalWithPalletsByObject.pending, (state, action) => {
                return {
                    ...state,
                    loading: true,
                    pallets: [],
                    modified: false,
                    modalShown: action.meta.arg.modalType,
                    error: null
                } as ReduxStateModalPallets2;
            })
            .addCase(showModalWithPalletsByObject.fulfilled, (state, action) => {
                let statusIds = _.uniq(action.payload.map(p => p.statusId));
                return {
                    ...state,
                    loading: false,
                    error: null,
                    pallets: action.payload.map(x => ({
                        ...x,
                        originalStatusId: x.statusId,
                        defects: x.defects.map(d => ({ ...d, randomDefectId: Guid.create().toString() }))
                    })),
                    modified: false,
                    originalStatus: statusIds.length === 1 ? statusIds[0] : -1,
                    modalShown: action.meta.arg.modalType
                } as ReduxStateModalPallets2;
            })
            .addCase(showModalWithPalletsByObject.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    pallets: [],
                    error: action.payload,
                    modified: false,
                    modalShown: action.meta.arg.modalType
                } as ReduxStateModalPallets2;
            });

        builder
            .addCase(showModalWithPalletsByGuid.pending, (state, action) => {
                return {
                    ...state,
                    loading: true,
                    pallets: [],
                    modified: false,
                    modalShown: action.meta.arg.modalType,
                    caller: action.meta.arg.callerModalType,
                    error: null
                } as ReduxStateModalPallets2;
            })
            .addCase(showModalWithPalletsByGuid.fulfilled, (state, action) => {
                let statusIds = _.uniq(action.payload.map(p => p.statusId));
                return {
                    ...state,
                    loading: false,
                    error: null,
                    pallets: action.payload.map(x => ({
                        ...x,
                        originalStatusId: x.statusId,
                        defects: x.defects.map(d => ({ ...d, randomDefectId: Guid.create().toString() }))
                    })),
                    modified: false,
                    originalStatus: statusIds.length === 1 ? statusIds[0] : -1,
                    modalShown: action.meta.arg.modalType,
                    caller: action.meta.arg.callerModalType
                } as ReduxStateModalPallets2;
            })
            .addCase(showModalWithPalletsByGuid.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    pallets: [],
                    error: action.payload,
                    modified: false,
                    modalShown: action.meta.arg.modalType,
                    caller: action.meta.arg.callerModalType
                } as ReduxStateModalPallets2;
            });

        builder
            .addCase(showModalWithConsumptionState.pending, (state, action) => {
                return {
                    ...state,
                    loading: true,
                    modalShown: ModalType.ConsumptionIssuing,
                    caller: ModalType.None,
                    error: null
                } as ReduxStateModalPallets2;
            })
            .addCase(showModalWithConsumptionState.fulfilled, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    modalShown: ModalType.ConsumptionIssuing,
                    caller: ModalType.None,
                    consumptionState: action.payload,
                } as ReduxStateModalPallets2;
            })
            .addCase(showModalWithConsumptionState.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    modalShown: ModalType.ConsumptionIssuing,
                    caller: ModalType.None,
                    consumptionState: null
                } as ReduxStateModalPallets2;
            });
    }
});

export const { showModalWithoutPallets, closeModalRedux, setModified, setModalPallets, setModalMessage, setModalError } =
    modalPallets2.actions;
export default modalPallets2.reducer;
